import {
  CircularProgress,
  debounce,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from "@material-ui/core"
import { GetApp } from "@material-ui/icons"
import AddIcon from "@material-ui/icons/Add"
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft"
import ChevronRightIcon from "@material-ui/icons/ChevronRight"
import EditIcon from "@material-ui/icons/Edit"
import VisibilityIcon from "@material-ui/icons/Visibility"
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { listAppUsers } from "~/actions/users"
import PermissionsWrapper from "~/components/Common/PermissionsWrapper"
import AddUserToCourse from "~/dialogs/AddUserToCourseDialog"
import AppContext from "~/contexts/App"
import UsersContext from "~/contexts/Users"
import dayjs from "dayjs"
import * as FileSaver from "file-saver"
import PropTypes from "prop-types"
import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import { useInfiniteQuery } from "react-query"

const columnHelper = createColumnHelper()

const columns = [
  columnHelper.accessor((row) => row.id, {
    id: "id",
    header: "ID",
  }),
  columnHelper.accessor((row) => row?.first_name, {
    first_name: "first_name",
    header: "First Name",
    cell: (props) => {
      const splitName = props?.row?.original?.first_name?.split(" ")
      return (
        <span className="flex flex-col">
          {splitName?.map((sso, i) => (
            <span key={i}>{sso}</span>
          ))}
        </span>
      )
    },
  }),
  columnHelper.accessor((row) => row.last_name, {
    last_name: "last_name",
    header: "Last Name",
  }),
  columnHelper.accessor((row) => row.username, {
    username: "username",
    header: "Username",
  }),
  columnHelper.accessor((row) => row.email, {
    email: "email",
    header: "Email",
  }),
  columnHelper.accessor((row) => row.client_name, {
    client_name: "client_name",
    header: "Client",
  }),
  columnHelper.accessor((row) => row.test_user, {
    test_user: "test_user",
    header: "Test User",
  }),
  columnHelper.accessor((row) => row.last_online, {
    last_online: "last_online",
    header: "Last Online",
    cell: (props) => {
      return (
        <span>
          {
            dayjs(props.row.original.last_online).format("MM/DD/YYYY HH:mm") // '25/01/2019'
          }
        </span>
      )
    },
  }),
  columnHelper.accessor((row) => row.created, {
    created: "created",
    header: "Created",
    cell: (props) => {
      return (
        <span>
          {
            dayjs(props.row.original.created).format("MM/DD/YYYY HH:mm") // '25/01/2019'
          }
        </span>
      )
    },
  }),
  columnHelper.accessor((row) => row.completed_courses, {
    completed_courses: "completed_courses",
    header: "Completed Courses",
  }),
  columnHelper.accessor((row) => row.in_progress_courses, {
    in_progress_courses: "in_progress_courses",
    header: "Courses in Progress",
  }),
  columnHelper.accessor(
    (row) => [
      row.github_id,
      row.amazon_id,
      row.facebook_id,
      row.google_id,
      row.meta_quest_id,
    ],
    {
      github_id: "github_id ",
      amazon_id: "amazon_id",
      facebook_id: "facebook_id",
      google_id: "google_id",
      meta_quest_id: "meta_quest_id",
      header: "Account Creation",
      cell: (props) => {
        const userSSOList = []

        if (props.row.original.github_id) {
          userSSOList.push("Github")
        }
        if (props.row.original.amazon_id) {
          userSSOList.push("Amazon")
        }
        if (props.row.original.facebook_id) {
          userSSOList.push("Facebook")
        }
        if (props.row.original.google_id) {
          userSSOList.push("Google")
        }
        if (props.row.original.meta_quest_id) {
          userSSOList.push("Meta Quest")
        }

        return (
          <span className="flex flex-col">
            {userSSOList?.length > 0
              ? userSSOList.map((sso, i) => <span key={i}>{sso}</span>)
              : "Email"}
          </span>
        )
      },
    },
  ),
]

function UsersTableNext({
  currentPage,
  setCurrentPage,
  accordionExpanded,
  setTotalNumber,
}) {
  const [pageSize, setPageSizeState] = useState(100)
  const [anchorEl, setAnchorEl] = useState(null)
  const wrapperRef = useRef()
  const footerRef = useRef()

  const {
    searchValue,
    activeValue,
    sort,
    sliderValue,
    setSliderValue,
    setDebouncedSliderValue,
    maxTakenCourseCountMutation,
    setCoursesCount,
    clientValue,
    userTypeValue,
    setSort,
    listAppUsersMutation,
    accountCreationValue,
  } = useContext(UsersContext)

  const { openDialog, authUser } = useContext(AppContext)

  let dataFetch = {
    sort: {
      key: sort.name,
      direction: sort.direction || "DESC",
    },
    filter: {
      search: searchValue,
      last_online: activeValue,
      completed_courses: sliderValue,
      client_id: clientValue === "all" ? null : clientValue,
      user_type: userTypeValue === "all" ? null : userTypeValue,
      account_creation:
        accountCreationValue === "all" ? null : accountCreationValue,
    },
    limit: pageSize,
  }

  useEffect(() => {
    maxTakenCourseCountMutation.mutate(
      {},
      {
        onSuccess: (res) => {
          setSliderValue([0, res])
          setDebouncedSliderValue([0, res])
          setCoursesCount(res)
        },
      },
    )
  }, [])

  let today = new Date().toISOString().slice(0, 10)

  const downloadCSV = (allData) => {
    let data = {}
    if (!allData) {
      data = { ...dataFetch, download: true }
    } else {
      data = {
        download: true,
        sort: {
          key: "id",
          direction: "DESC",
        },
        filter: {
          search: "",
          last_online: "all",
          completed_courses: [0, 39],
        },
        limit: 100,
      }
    }

    listAppUsersMutation.mutate(data, {
      onSuccess: (res) => {
        var file = new Blob([res], { type: "text/csv" })

        FileSaver.saveAs(file, `users(${today}).csv`)
        setAnchorEl(null)
      },
    })
  }

  const { data, fetchNextPage, isFetching, refetch, hasNextPage } =
    useInfiniteQuery({
      queryKey: ["appUsers", dataFetch],
      queryFn: async ({ pageParam = 0 }) => {
        return await listAppUsers({ ...dataFetch, offset: pageParam })
      },
      enabled: !!sliderValue[1],
      getNextPageParam: (lastPage, allPages) => {
        const nextPageData = lastPage.nextOffset

        return nextPageData
      },
    })

  // useEffect(() => {
  //   const updateMaxHeight = () => {
  //     wrapperRef.current.style.maxHeight = `${
  //       wrapperRef.current.parentNode.clientHeight - 64
  //     }px`
  //   }
  //   updateMaxHeight()
  //   window.addEventListener("resize", debounce(updateMaxHeight, 100))
  //   return () => window.removeEventListener("resize", updateMaxHeight)
  // }, [isFetching, accordionExpanded])

  useEffect(() => {
    const debouncedResize = debounce(() => {
      wrapperRef.current.style.maxHeight = `${
        wrapperRef.current.parentNode.clientHeight - 64 - 64
      }px`
    }, 100)

    function resize() {
      debouncedResize()
    }
    resize()
    window.addEventListener("resize", resize)
    return () => window.removeEventListener("resize", resize)
  }, [isFetching, accordionExpanded])

  const tableData = useMemo(() => {
    return data?.pages[currentPage]?.data
  }, [data, currentPage])

  const hasPreviousPage = useMemo(() => {
    return currentPage === 0 ? false : true
  }, [currentPage])

  const { getHeaderGroups, getRowModel } = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  const handleChange = (event) => {
    let value = event.target.value
    setPageSizeState(value)
  }

  const editUser = async (data) => {
    const preClientId = data.client_id
    const preTestUser = data.test_user

    const res = await openDialog("editUserDialog", data, false)

    if (preClientId !== res.client_id || preTestUser !== res.test_user) {
      refetch({ refetchPage: (page, index) => index === currentPage })
    }
  }

  const handleOpenExportMenu = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleCloseExport = () => {
    setAnchorEl(null)
  }

  const renderLoading = () => {
    if (isFetching) {
      return (
        <div className="relative top-0 left-0 h-full w-full z-20 flex flex-col items-center justify-center gap-4">
          <CircularProgress size={50} />
          <p className="text-xl">Loading...</p>
        </div>
      )
    } else if (tableData?.length === 0) {
      return (
        <div className="relative top-0 left-0 h-full w-full z-20 flex flex-col items-center justify-center gap-4">
          <p className="text-2xl">No Results with this sort</p>
        </div>
      )
    }
    return null
  }

  const renderPageCounts = () => {
    const page = data?.pages[currentPage]
    if (!page) {
      return null
    }
    const start = currentPage * pageSize
    return (
      <>
        {page.total > 0 ? start + 1 : 0} -{" "}
        {start + pageSize < page.total ? start + pageSize : page.total} out of{" "}
        {page.total.toLocaleString()}
      </>
    )
  }

  useEffect(() => {
    setTotalNumber(data?.pages[currentPage]?.total)
  }, [data])

  return (
    <div className="relative h-full">
      <AddUserToCourse />
      <div
        ref={wrapperRef}
        className="overflow-auto h-full"
        // style={{ maxHeight: "900px" }}
      >
        {isFetching || !tableData ? (
          renderLoading()
        ) : (
          <table className="w-full">
            <thead className="sticky top-0 bg-white z-10">
              {getHeaderGroups().map((headerGroup, i) => (
                <tr key={headerGroup.id + i} id={headerGroup.id + i}>
                  <th className="w-[100px] px-4 py-2 text-left text-xs uppercase tracking-wide text-gray-500 font-semibold border-b border-gray-200">
                    Actions
                  </th>
                  {headerGroup.headers.map((column, i) => (
                    <th
                      key={column.id + i}
                      id={column.id + i}
                      scope="col"
                      className="min-w-[100px] px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-200"
                    >
                      {flexRender(
                        column.column.columnDef.header,
                        column.getContext(),
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="bg-white">
              {getRowModel()?.rows.map((row, i) => (
                <tr
                  key={row.id + i}
                  className={i % 2 !== 0 ? "bg-white" : "bg-gray-100"}
                >
                  <td className="flex relative pr-3">
                    {authUser.permission_group === "super" && (
                      <IconButton
                        color="secondary"
                        className="h-full"
                        onClick={() =>
                          openDialog("addUserToCourse", {
                            data: [row.original],
                          })
                        }
                      >
                        <AddIcon />
                      </IconButton>
                    )}
                    <IconButton
                      onClick={() =>
                        openDialog("userDetailsDialog", row.original)
                      }
                      color="secondary"
                      className="h-full"
                    >
                      <VisibilityIcon />
                    </IconButton>
                    <PermissionsWrapper permissionGroup="admin">
                      <IconButton
                        onClick={() => {
                          editUser(row.original)
                        }}
                        color="secondary"
                        className="h-full"
                      >
                        <EditIcon />
                      </IconButton>
                    </PermissionsWrapper>
                  </td>
                  {row.getVisibleCells().map((cell, i) => (
                    <td
                      key={cell.id + i}
                      className="whitespace-nowrap px-3 py-1 text-sm text-black-500"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {!isFetching && tableData && (
        <div className="bg-white border-t-1 border-gray-300 flex gap-6 py-2 z-30">
          <div className="flex gap-4 pl-2">
            <PermissionsWrapper permissionGroup="admin">
              <Tooltip title="Download data">
                <IconButton onClick={handleOpenExportMenu}>
                  <GetApp className="text-[#50cdd8]" />
                </IconButton>
              </Tooltip>
            </PermissionsWrapper>
            <Menu
              id="admin-dropdown"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              getContentAnchorEl={null}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleCloseExport}
            >
              <MenuItem onClick={() => downloadCSV("allData")}>
                All Data
              </MenuItem>
              <MenuItem onClick={() => downloadCSV()}>Current Data</MenuItem>
            </Menu>
          </div>
          <div className="flex items-center gap-4 pr-8">
            <select
              id="page_size_select"
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 mx-2 relative text-center px-2 py-1"
              onChange={(e) => {
                handleChange(e)
                setCurrentPage(0)
              }}
              value={pageSize}
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
            <button
              disabled={!hasPreviousPage}
              onClick={() => {
                setCurrentPage(currentPage - 1)
              }}
              className={
                hasPreviousPage
                  ? "w-6 h-10 bg-transparent text-black text-xs hover:text-[#50cdd8]"
                  : "w-6 h-10 bg-transparent opacity-40 text-black text-xs cursor-default"
              }
            >
              <ChevronLeftIcon />
            </button>
            <p className="whitespace-nowrap">{renderPageCounts()}</p>
            <button
              disabled={!hasNextPage}
              onClick={() => {
                if (currentPage === data.pageParams.length - 1) {
                  fetchNextPage().then(() => setCurrentPage(currentPage + 1))
                } else {
                  setCurrentPage(currentPage + 1)
                }
              }}
              className={
                hasNextPage
                  ? "w-6 h-10 bg-transparent text-black text-xs hover:text-[#50cdd8]"
                  : "w-6 h-10 bg-transparent opacity-40 text-black text-xs cursor-default"
              }
            >
              <ChevronRightIcon />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

UsersTableNext.propTypes = {
  setCurrentPage: PropTypes.func,
  currentPage: PropTypes.number,
  accordionExpanded: PropTypes.bool,
  setTotalNumber: PropTypes.func,
  row: PropTypes.object,
}

export default UsersTableNext
